import { gql, TypedDocumentNode, useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import { GET_PROSPECT_PROGRAM_QUERY } from "../../../../pages/people/[pgaId]/person/queries";
import type { PATExemptTourValue } from "../../utils/pat";

type PatCreateHandicapSubmissionInput = {
  personId: string;
  handicap: number;
  note?: string;
  document?: {
    name: string;
    file: File;
  };
};

type PatCreateHandicapSubmissionResponse = {
  patCreateHandicapSubmission: {
    success: boolean;
    message?: string;
  };
};

const PAT_CREATE_HANDICAP_SUBMISSION_MUTATION: TypedDocumentNode<
  PatCreateHandicapSubmissionResponse,
  { input: PatCreateHandicapSubmissionInput }
> = gql`
  mutation PatCreateHandicapSubmission($input: PATHandicapSubmissionInput!) {
    patCreateHandicapSubmission(input: $input) {
      success
      message
    }
  }
`;

type PatCreateExemptionSubmissionInput = {
  personId: string;
  tour: PATExemptTourValue;
  note?: string;
  document?: {
    name: string;
    file: File;
  };
};

type PatCreateExemptionSubmissionResponse = {
  patCreateExemptionSubmission: {
    success: boolean;
    message?: string;
  };
};

const PAT_CREATE_TOUR_EXEMPTION_SUBMISSION_MUTATION: TypedDocumentNode<
  PatCreateExemptionSubmissionResponse,
  { input: PatCreateExemptionSubmissionInput }
> = gql`
  mutation PatCreateExemptionSubmission($input: PATExemptionSubmissionInput!) {
    patCreateExemptionSubmission(input: $input) {
      success
      message
    }
  }
`;

type PatCreateAlternateEventSubmissionInput = {
  personId: string;
  eventName: string;
  eventDate: string; // ISO8601Date format (YYYY-MM-DD)
  first18HoleScore: number;
  second18HoleScore: number;
  targetScore: number;
  note?: string;
  document?: {
    name: string;
    file: File;
  };
  combinedEvents: boolean;
};

type PatCreateAlternateEventSubmissionResponse = {
  patCreateAlternateEventSubmission: {
    success: boolean;
    message?: string;
  };
};

const PAT_CREATE_ALTERNATE_EVENT_SUBMISSION_MUTATION: TypedDocumentNode<
  PatCreateAlternateEventSubmissionResponse,
  { input: PatCreateAlternateEventSubmissionInput }
> = gql`
  mutation PatCreateAlternateEventSubmission($input: PATAlternateEventSubmissionInput!) {
    patCreateAlternateEventSubmission(input: $input) {
      success
      message
    }
  }
`;

type PatUpdateHandicapSubmissionInput = {
  id: string;
  handicap?: number;
  note?: string;
  document?: {
    name: string;
    file: File;
    category?: string;
  };
};

type PatUpdateHandicapSubmissionResponse = {
  patUpdateHandicapSubmission: {
    success: boolean;
    message?: string;
    code: string;
  };
};

const PAT_UPDATE_HANDICAP_SUBMISSION_MUTATION: TypedDocumentNode<
  PatUpdateHandicapSubmissionResponse,
  { input: PatUpdateHandicapSubmissionInput }
> = gql`
  mutation PatUpdateHandicapSubmission($input: PATUpdateHandicapSubmissionInput!) {
    patUpdateHandicapSubmission(input: $input) {
      success
      message
      code
    }
  }
`;

type PatUpdateExemptionSubmissionInput = {
  id: string;
  tour?: PATExemptTourValue;
  note?: string;
  document?: {
    name: string;
    file: File;
    category?: string;
  };
};

type PatUpdateExemptionSubmissionResponse = {
  patUpdateExemptionSubmission: {
    success: boolean;
    message?: string;
    code: string;
  };
};

const PAT_UPDATE_TOUR_EXEMPTION_SUBMISSION_MUTATION: TypedDocumentNode<
  PatUpdateExemptionSubmissionResponse,
  { input: PatUpdateExemptionSubmissionInput }
> = gql`
  mutation PatUpdateExemptionSubmission($input: PATUpdateExemptionSubmissionInput!) {
    patUpdateExemptionSubmission(input: $input) {
      success
      message
      code
    }
  }
`;

type PatUpdateAlternateEventSubmissionInput = {
  id: string;
  eventName?: string;
  eventDate?: string; // ISO8601Date format (YYYY-MM-DD)
  first18HoleScore?: number;
  second18HoleScore?: number;
  targetScore?: number;
  note?: string;
  document?: {
    name: string;
    file: File;
    category?: string;
  };
  combinedEvents?: boolean;
};

type PatUpdateAlternateEventSubmissionResponse = {
  patUpdateAlternateEventSubmission: {
    success: boolean;
    message?: string;
    code: string;
  };
};

const PAT_UPDATE_ALTERNATE_EVENT_SUBMISSION_MUTATION: TypedDocumentNode<
  PatUpdateAlternateEventSubmissionResponse,
  { input: PatUpdateAlternateEventSubmissionInput }
> = gql`
  mutation PatUpdateAlternateEventSubmission($input: PATUpdateAlternateEventSubmissionInput!) {
    patUpdateAlternateEventSubmission(input: $input) {
      success
      message
      code
    }
  }
`;

type PatDeleteSubmissionResponse = {
  patDeleteSubmission: {
    success: boolean;
    message?: string;
  };
};

export const PAT_DELETE_SUBMISSION_MUTATION: TypedDocumentNode<PatDeleteSubmissionResponse, { id: string }> = gql`
  mutation PatDeleteSubmission($id: ID!) {
    patDeleteSubmission(id: $id) {
      success
      message
    }
  }
`;

interface UsePATMutationsOptions {
  pgaId: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

export function usePATMutations({ pgaId, onSuccess, onError }: UsePATMutationsOptions) {
  const { enqueueSnackbar } = useSnackbar();

  const commonOptions = {
    onError: (error: Error) => {
      onError(error.message);
    },
    refetchQueries: [
      {
        query: GET_PROSPECT_PROGRAM_QUERY,
        variables: { pgaId }
      }
    ],
    awaitRefetchQueries: true
  };

  // Create mutations
  const [createHandicapSubmission, { loading: loadingCreateHandicap }] = useMutation(
    PAT_CREATE_HANDICAP_SUBMISSION_MUTATION,
    {
      ...commonOptions,
      onCompleted: (data) => {
        if (data?.patCreateHandicapSubmission?.success) {
          enqueueSnackbar(data.patCreateHandicapSubmission.message, { variant: "default" });
          onSuccess();
        } else {
          onError(data?.patCreateHandicapSubmission?.message || "Failed to create handicap submission");
        }
      }
    }
  );

  const [createExemption, { loading: loadingCreateExemption }] = useMutation(
    PAT_CREATE_TOUR_EXEMPTION_SUBMISSION_MUTATION,
    {
      ...commonOptions,
      onCompleted: (data) => {
        if (data?.patCreateExemptionSubmission?.success) {
          enqueueSnackbar(data.patCreateExemptionSubmission.message, { variant: "default" });
          onSuccess();
        } else {
          onError(data?.patCreateExemptionSubmission?.message || "Failed to create PAT exemption");
        }
      }
    }
  );

  const [createAlternateEvent, { loading: loadingCreateAlternateEvent }] = useMutation(
    PAT_CREATE_ALTERNATE_EVENT_SUBMISSION_MUTATION,
    {
      ...commonOptions,
      onCompleted: (data) => {
        if (data?.patCreateAlternateEventSubmission?.success) {
          enqueueSnackbar(data.patCreateAlternateEventSubmission.message, { variant: "default" });
          onSuccess();
        } else {
          onError(data?.patCreateAlternateEventSubmission?.message || "Failed to create alternate event submission");
        }
      }
    }
  );

  // Update mutations
  const [updateHandicapSubmission, { loading: loadingUpdateHandicap }] = useMutation(
    PAT_UPDATE_HANDICAP_SUBMISSION_MUTATION,
    {
      ...commonOptions,
      onCompleted: (data) => {
        if (data?.patUpdateHandicapSubmission?.success) {
          enqueueSnackbar(data.patUpdateHandicapSubmission.message, { variant: "default" });
          onSuccess();
        } else {
          onError(data?.patUpdateHandicapSubmission?.message || "Failed to update handicap submission");
        }
      }
    }
  );

  const [updateExemption, { loading: loadingUpdateExemption }] = useMutation(
    PAT_UPDATE_TOUR_EXEMPTION_SUBMISSION_MUTATION,
    {
      ...commonOptions,
      onCompleted: (data) => {
        if (data?.patUpdateExemptionSubmission?.success) {
          enqueueSnackbar(data.patUpdateExemptionSubmission.message, { variant: "default" });
          onSuccess();
        } else {
          onError(data?.patUpdateExemptionSubmission?.message || "Failed to update PAT exemption");
        }
      }
    }
  );

  const [updateAlternateEvent, { loading: loadingUpdateAlternateEvent }] = useMutation(
    PAT_UPDATE_ALTERNATE_EVENT_SUBMISSION_MUTATION,
    {
      ...commonOptions,
      onCompleted: (data) => {
        if (data?.patUpdateAlternateEventSubmission?.success) {
          enqueueSnackbar(data.patUpdateAlternateEventSubmission.message, { variant: "default" });
          onSuccess();
        } else {
          onError(data?.patUpdateAlternateEventSubmission?.message || "Failed to update alternate event submission");
        }
      }
    }
  );
  const [deleteSubmission, { loading: loadingDeleteSubmission }] = useMutation(PAT_DELETE_SUBMISSION_MUTATION, {
    ...commonOptions,
    onCompleted: (data) => {
      if (data?.patDeleteSubmission?.success) {
        enqueueSnackbar(data.patDeleteSubmission.message, { variant: "default" });
        onSuccess();
      } else {
        onError(data?.patDeleteSubmission?.message || "Failed to delete PAT submission");
      }
    }
  });

  const loading =
    loadingCreateHandicap ||
    loadingCreateExemption ||
    loadingCreateAlternateEvent ||
    loadingUpdateHandicap ||
    loadingUpdateExemption ||
    loadingUpdateAlternateEvent ||
    loadingDeleteSubmission;

  return {
    createHandicapSubmission,
    createExemption,
    createAlternateEvent,
    updateHandicapSubmission,
    updateExemption,
    updateAlternateEvent,
    deleteSubmission,
    loading
  };
}
